import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  AUTH_EXPIRED_EVENT,
  AUTH_STORAGE_KEY,
  clearStoredAuth,
  getValidStoredAuth,
  setStoredAuth,
} from "../utils/storage";
import { clearControlPanelUnlock } from "../utils/controlPanelStorage";
import { authService } from "../services/auth.service";
import { clearLocationPermissionSession } from "../constants/locationPermission";
import { wipeOfflineCaches } from "../offline/offlineSyncManager";
import {
  WORK_MODE,
  WORK_MODE_CHANGE_EVENT,
  WORK_MODE_STORAGE_KEY,
  isOfflineWorkMode,
} from "../constants/workMode";

const AuthContext = createContext(null);

const EMPTY_AUTH = {
  user: null,
  token: null,
  isAuthenticated: false,
};

function readPersistedAuth() {
  const saved = getValidStoredAuth();
  if (!saved) return EMPTY_AUTH;
  const permissions = Array.isArray(saved?.permissions) ? saved.permissions : [];
  const user = saved.user ? { ...saved.user, permissions } : null;
  return {
    user,
    token: saved.token,
    isAuthenticated: true,
    permissions,
  };
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readPersistedAuth);
  const [isReady, setIsReady] = useState(() => !getValidStoredAuth()?.token);

  const syncPermissions = async (token, userOverride) => {
    if (!token) return [];
    if (userOverride?.role === "SSDI_SUPER_ADMIN") {
      const nextUser = {
        ...(userOverride || auth?.user || {}),
        permissions: [],
        customRole: null,
        dataScope: null,
        elevated: false,
      };
      const nextAuth = {
        user: nextUser,
        token,
        isAuthenticated: true,
        permissions: [],
      };
      setStoredAuth({ user: nextUser, token, permissions: [] });
      setAuth(nextAuth);
      return [];
    }
    try {
      const response = await authService.getEffectivePermissions(token);
      const permissions = Array.isArray(response?.permissions)
        ? response.permissions
        : [];
      const nextUser = {
        ...(userOverride || auth?.user || {}),
        permissions,
        customRole: response?.customRole || null,
        dataScope: response?.dataScope || null,
        elevated: Boolean(response?.elevated),
      };
      const nextAuth = {
        user: nextUser,
        token,
        isAuthenticated: true,
        permissions,
      };
      setStoredAuth({ user: nextUser, token, permissions });
      setAuth(nextAuth);
      return permissions;
    } catch {
      const nextUser = {
        ...(userOverride || auth?.user || {}),
        permissions: [],
        customRole: null,
        dataScope: null,
        elevated: false,
      };
      const nextAuth = {
        user: nextUser,
        token,
        isAuthenticated: true,
        permissions: [],
      };
      setStoredAuth({ user: nextUser, token, permissions: [] });
      setAuth(nextAuth);
      return [];
    }
  };

  const login = async ({ user, token }) => {
    const normalizedUser = {
      ...user,
      id: user?.id || user?._id || null,
      permissions: Array.isArray(user?.permissions) ? user.permissions : [],
    };

    if (
      !token ||
      normalizedUser.role === "BANK_ADMIN" ||
      normalizedUser.role === "BANK_PERSON"
    ) {
      setStoredAuth({ user: normalizedUser, token, permissions: normalizedUser.permissions });
      setAuth({
        user: normalizedUser,
        token,
        isAuthenticated: true,
        permissions: normalizedUser.permissions,
      });
      return normalizedUser.permissions;
    }

    return syncPermissions(token, normalizedUser);
  };

  const logout = () => {
    clearControlPanelUnlock();
    clearLocationPermissionSession();
    try {
      window.localStorage.setItem(WORK_MODE_STORAGE_KEY, WORK_MODE.ONLINE);
      window.dispatchEvent(
        new CustomEvent(WORK_MODE_CHANGE_EVENT, { detail: WORK_MODE.ONLINE })
      );
    } catch {
      /* ignore */
    }
    wipeOfflineCaches().catch(() => {});
    clearStoredAuth();
    setAuth(EMPTY_AUTH);
  };

  const patchUser = useCallback((partial) => {
    setAuth((prev) => {
      if (!prev?.user) return prev;
      const nextUser = {
        ...prev.user,
        ...partial,
        id: partial?.id || partial?._id || prev.user.id || prev.user._id,
      };
      const same =
        nextUser.name === prev.user.name &&
        nextUser.phone === prev.user.phone &&
        nextUser.id === prev.user.id &&
        nextUser._id === prev.user._id;
      if (same && Object.keys(partial || {}).every((key) => nextUser[key] === prev.user[key])) {
        return prev;
      }
      setStoredAuth({ user: nextUser, token: prev.token });
      return { ...prev, user: nextUser };
    });
  }, []);

  useEffect(() => {
    const saved = readPersistedAuth();
    setAuth(saved);

    if (!saved.token) {
      setIsReady(true);
      return undefined;
    }

    if (isOfflineWorkMode()) {
      setIsReady(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await authService.refreshSession(saved.token);
        if (cancelled || !data?.token) return;
        await login({
          user: data.user || saved.user,
          token: data.token,
        });
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401 && !isOfflineWorkMode()) {
          logout();
        }
      } finally {
        if (!cancelled) setIsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onExpired = () => logout();
    const onStorage = (event) => {
      if (event.key && event.key !== AUTH_STORAGE_KEY) return;
    const saved = getValidStoredAuth();
    if (saved?.token && saved?.user) {
      setAuth({
        user: saved.user,
        token: saved.token,
        isAuthenticated: true,
        permissions: Array.isArray(saved.permissions) ? saved.permissions : [],
      });
      return;
    }
    setAuth(EMPTY_AUTH);
  };

    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const value = useMemo(
    () => ({
      auth,
      isReady,
      isLoading: !isReady,
      isAuthenticated: Boolean(auth?.isAuthenticated),
      user: auth?.user || null,
      login,
      logout,
      setAuth,
      patchUser,
    }),
    [auth, isReady, patchUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  return useContext(AuthContext);
}
